import { TFunction } from "i18next";
import { toDataField } from "../registration/provider/registrationFormProviderUtils";
import {
	RuleResult,
	evaluateRuleSetForDataFields,
	evaluateRuleSetForSections,
	evaluateRuleSetForSteps,
} from "../registration/rules/ruleSets/ruleSet";
import {
	DataFieldType,
	GuestResource,
	IConsent,
	RegistrationFormSectionChildrenGuestAttribute,
	RegistrationFormSectionChildrenResourceEntityTypeGuestAttribute,
	RegistrationFormSectionResource,
	RegistrationFormSectionResourceBase,
	RegistrationFormSectionResourceTypeDefaultSection,
	RegistrationFormStaticStepResource,
	RegistrationFormStepChildrenResourceEntityTypeSection,
	RegistrationFormStepResource,
	RegistrationGuestAttribute,
	RuleSetResource,
} from "../shared/types";
import { DataFieldValues } from "../shared/types/guest";
import { RegistrationFormConsentBody, RegistrationFormResource } from "../shared/types/registrationForm";
import { ConsentValues, FilledSteps } from "./provider/registrationFormProviderContext";

export const flattenStepDataFields = (filledSteps: Record<number, Record<number, any>>) =>
	Object.values(filledSteps)
		.map((stepValues) =>
			Object.keys(stepValues).reduce(
				(all, dataFieldId) => ({
					...all,
					[dataFieldId]: stepValues[+dataFieldId],
				}),
				{} as Record<string, any>,
			),
		)
		.reduce(
			(all, values) => ({
				...all,
				...values,
			}),
			{} as Record<string, any>,
		);

export const getSectionsFromStep = (step: RegistrationFormStepResource) =>
	step.children.filter((x) => x.entityType === "section") as RegistrationFormStepChildrenResourceEntityTypeSection[];

export const getSectionsFromStaticStep = (step: RegistrationFormStaticStepResource) =>
	step.children.filter(
		(x) => x.entityType === "section",
	) as RegistrationFormStepChildrenResourceEntityTypeSection<RegistrationFormSectionResourceTypeDefaultSection>[];

export const toGuestAttributeChildrenFromSection = (section: RegistrationFormSectionResourceBase) =>
	section.children
		.filter((x) => x.entityType === "guestAttribute")
		.map((x) => (x as RegistrationFormSectionChildrenResourceEntityTypeGuestAttribute).guestAttribute);

export const createInitialValues = (
	guestAttributes: RegistrationFormSectionChildrenGuestAttribute[],
	guestAttributeValues?: RegistrationGuestAttribute[],
	isCompanion?: boolean,
): DataFieldValues =>
	guestAttributes.reduce((acc, guestAttribute) => {
		const dataField = guestAttribute.dataField;
		const guestAttributeValue =
			guestAttributeValues && guestAttributeValues.find((value) => value.id === guestAttribute.id);
		const key = isCompanion ? `companion_${dataField.id}` : dataField.id;

		let value;
		switch (dataField.type) {
			case DataFieldType.CHECKBOX_GROUP:
				value = guestAttributeValue && guestAttributeValue.value !== null ? guestAttributeValue.value : [];
				break;
			case DataFieldType.MULTI_SELECT_FIELD:
				value = guestAttributeValue && guestAttributeValue.value !== null ? guestAttributeValue.value : [];
				break;
			case DataFieldType.RADIO:
				value = guestAttributeValue && guestAttributeValue.value !== null ? `${guestAttributeValue.value}` : null;
				break;
			case DataFieldType.CHECKBOX:
				value =
					guestAttributeValue && guestAttributeValue.value !== null ? (guestAttributeValue.value as boolean) : false;
				break;
			default:
				value = guestAttributeValue?.value ?? null;
				break;
		}

		return {
			...acc,
			[key]: value,
		};
	}, {});

export const toConsentValues = (consents: IConsent[], givenConsents: ConsentValues = {}) => {
	return consents
		.filter((consent) => consent.type != "notice")
		.reduce(
			(previousValue, currentValue) => ({
				...previousValue,
				[currentValue.id]: (givenConsents[currentValue.id] ?? currentValue.type === "checkbox") ? false : "",
			}),
			{},
		);
};

export const toConsentErrors = (consents: IConsent[], values: ConsentValues, t: TFunction) => {
	return consents.reduce((res, c) => {
		const id = c.id.toString();
		if (!values[id] && c.required) {
			return {
				...res,
				[id]: t("Field is required"),
			};
		}
		return res;
	}, {});
};

export const mapConsentValuesToBody = (consents: ConsentValues): RegistrationFormConsentBody[] => {
	return Object.keys(consents).map((id) => ({
		id: id,
		value: consents[id] === "true" || consents[id] === true,
	}));
};

export const getStepsVisibility = (
	ruleSets: RuleSetResource[],
	filledSteps: FilledSteps,
	guest: GuestResource | null,
	steps: RegistrationFormStepResource[],
) => {
	const dataFieldValues = flattenStepDataFields(filledSteps);

	return mergeRuleResults(
		ruleSets.flatMap((ruleSet) => evaluateRuleSetForSteps(ruleSet, dataFieldValues, guest, filledSteps, steps)),
	);
};

export const getSectionsVisibility = (
	steps: RegistrationFormStepResource[],
	ruleSets: RuleSetResource[],
	filledSteps: FilledSteps,
	guest: GuestResource | null,
) => {
	const dataFieldValues = flattenStepDataFields(filledSteps);
	const hiddenSteps = getStepsVisibility(ruleSets, filledSteps, guest, steps);
	const sectionsInHiddenSteps = steps
		.filter((x) => hiddenSteps.includes(x.id))
		.flatMap((x) =>
			x.children.reduce((acc, x) => [...acc, ...(x.entityType === "section" ? [x.section.id] : [])], [] as number[]),
		);
	const _hiddenSections = mergeRuleResults(
		ruleSets.flatMap((ruleSet) => evaluateRuleSetForSections(ruleSet, dataFieldValues, guest, filledSteps, steps)),
	);

	return [...sectionsInHiddenSteps, ..._hiddenSections];
};

export const getDataFieldsVisibility = (
	steps: RegistrationFormStepResource[],
	ruleSets: RuleSetResource[],
	filledSteps: FilledSteps,
	guest: GuestResource | null,
): string[] => {
	const dataFieldValues = flattenStepDataFields(filledSteps);
	const hiddenSections = getSectionsVisibility(steps, ruleSets, filledSteps, guest);
	const dataFieldsInHiddenSections = steps.flatMap((registrationStep) =>
		registrationStep.children.reduce((acc, stepChild) => {
			if (
				stepChild.entityType === "section" &&
				stepChild.section.type !== "companion" &&
				hiddenSections.includes(stepChild.section.id)
			) {
				return stepChild.section.children.reduce((acc, sectionChild) => {
					if (sectionChild.entityType === "guestAttribute") {
						return [...acc, `${stepChild.section.id}-${sectionChild.guestAttribute.dataField.id}`];
					}
					return acc;
				}, acc);
			}
			return acc;
		}, [] as string[]),
	);

	const _hiddenDataFields = mergeRuleResults(
		ruleSets.flatMap((ruleSet) => evaluateRuleSetForDataFields(ruleSet, dataFieldValues, guest, filledSteps, steps)),
	);
	return [...dataFieldsInHiddenSections, ..._hiddenDataFields];
};

export function findNextStep(
	hiddenSteps: number[],
	steps: RegistrationFormStepResource[],
	stepId: number | null,
): number | null {
	if (stepId === null) {
		const firstStep = steps[0];
		if (hiddenSteps.includes(firstStep.id)) {
			return findNextStep(hiddenSteps, steps, firstStep.id);
		}
		return firstStep.id;
	}
	const idx = steps.findIndex((x) => x.id === stepId);

	if (idx + 1 > steps.length - 1) {
		return null;
	}
	const nextStep = steps[idx + 1];
	if (hiddenSteps.includes(nextStep.id)) {
		return findNextStep(hiddenSteps, steps, nextStep.id);
	}
	return nextStep.id;
}

export function findPrevStep(
	hiddenSteps: number[],
	steps: RegistrationFormStepResource[],
	stepId: number | null,
): number | null {
	if (stepId === null) {
		const lastStep = steps[steps.length - 1];
		if (hiddenSteps.includes(lastStep.id)) {
			return findPrevStep(hiddenSteps, steps, lastStep.id);
		}
		return lastStep.id;
	}
	const idx = steps.findIndex((x) => x.id === stepId);

	if (idx - 1 === -1) {
		return null;
	}
	const prevStep = steps[idx - 1];
	if (hiddenSteps.includes(prevStep.id)) {
		return findPrevStep(hiddenSteps, steps, prevStep.id);
	}
	return prevStep.id;
}

const mergeRuleResults = (ruleResults: RuleResult[]) => {
	const resultMap = ruleResults.reduce(
		(res, step) => {
			if (!res[step.id]) {
				res[step.id] = step.show;
			}
			return res;
		},
		{} as { [key: number | string]: boolean },
	);

	return Object.keys(resultMap)
		.map((k: any) => ({ id: k, show: resultMap[k] }))
		.filter((r) => !r.show)
		.map((r) => (r.id.toString().includes("-") ? r.id : Number(r.id)));
};

export const getDataFieldsFromForm = (registrationForm: RegistrationFormResource) =>
	registrationForm.steps
		.flatMap(getSectionsFromStep)
		.filter((section) => section.section.type === "default")
		.flatMap((x) => toGuestAttributeChildrenFromSection(x.section))
		.map(toDataField);

export const isSectionHidden = (
	registrationForm: RegistrationFormResource,
	sectionType: RegistrationFormSectionResource["type"],
	filledSteps: FilledSteps,
) => {
	const hiddenSections = getSectionsVisibility(
		registrationForm.steps,
		registrationForm.ruleSets,
		filledSteps,
		registrationForm.guest,
	);
	const sectionId = registrationForm.steps
		.flatMap(getSectionsFromStep)
		.find((x) => x.entityType === "section" && x.section.type === sectionType)?.section.id;
	return sectionId === undefined || hiddenSections.includes(sectionId);
};
